import classNames from "classnames";
import { Field, Form, Formik, FormikConfig, FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import style from "./mcs.scss";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { McsTableData } from "domain/mc";
import { hybridMcService } from "services/hybrid-mc/HybridMcService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    uuid: string;
    name: string;
    description?: string;
    handleSubmit: (values: FormValues) => Promise<void>;
    handleCancel: () => void;
    tableState: McsTableData[];
}

export interface FormValues {
    uuid: string;
    name: string;
    description: string;
}

export default function EditMcsForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = React.useState<boolean>(false);
    const abortController = new AbortController();

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (
        values: FormValues,
        { setErrors, setSubmitting }
    ) => {
        setSubmitting(false);
        if (values.name !== null) {
            setLoading(true);
            if (values.name !== props.name) {
                const matchingMcName = await hybridMcService.fetchData(abortController, { name: values.name });
                if (matchingMcName.mcdata.length === 0) {
                    await props.handleSubmit(values);
                    setSubmitting(true);
                } else {
                    setLoading(false);
                    setErrors({ name: t("Mcs.edit.errorMessage") });
                }
            } else {
                await props.handleSubmit(values);
                setSubmitting(true);
            }
        }
    };

    return (
        <Formik
            initialValues={{
                uuid: props.uuid,
                name: props.name,
                description: props.description,
            }}
            onSubmit={submitHandler}
            validationSchema={object().shape({
                name: string().required().max(50),
                description: string().optional().max(1000).nullable(),
            })}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({
                values,
                handleChange,
                handleBlur,
                errors,
                isSubmitting,
                isValid,
                dirty,
                touched,
            }: FormikProps<FormValues>) => {
                if (isSubmitting) {
                    return <LoadingIndicator />;
                }

                const loader = loading ? (
                    <div className={style.loaderContainer}>
                        <LoadingIndicator small={true} />
                    </div>
                ) : null;
                return (
                    <Form>
                        <div>
                            <div className={form.formFields}>
                                <label htmlFor="uuid" className={form.label}>
                                    {t("Mcs.edit.uuid")}
                                </label>
                                <span data-testid={testIds.workArea.mcs.editMcDialog.uuidLabel}>{values.uuid}</span>
                            </div>
                            <div className={form.formFields}>
                                <label htmlFor="name" className={form.label}>
                                    {t("Mcs.table.name")}
                                </label>
                                <div className={form.formFieldsColumnDirection}>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        className={classNames(form.input, form.fixedWidthInput)}
                                        autoFocus
                                        onBlur={handleBlur}
                                        defaultValue={values.name}
                                        onChange={handleChange}
                                        data-testid={testIds.workArea.mcs.editMcDialog.nameInput.itself}
                                    />
                                    {loader}
                                    <div
                                        className={form.error}
                                        data-testid={testIds.workArea.mcs.editMcDialog.nameInput.errorLabel}
                                    >
                                        {touched.name && errors.name}
                                    </div>
                                </div>
                            </div>

                            <div className={form.formFields}>
                                <div className={form.formFieldsColumnDirection}>
                                    <label htmlFor="description" className={classNames(form.label)}>
                                        {t("Mcs.table.description")}
                                    </label>
                                    <span className={form.optional}>{t("Common.optional")}</span>
                                </div>
                                <div className={form.formFieldsColumnDirection}>
                                    <Field
                                        as="textarea"
                                        id="description"
                                        className={classNames(form.input, form.fixedWidthInput, style.textarea)}
                                        name="description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        data-testid={testIds.workArea.mcs.editMcDialog.descriptionInput.itself}
                                    />
                                    <div
                                        className={form.error}
                                        data-testid={testIds.workArea.mcs.editMcDialog.descriptionInput.errorLabel}
                                    >
                                        {touched.description && errors.description}
                                    </div>
                                </div>
                            </div>

                            <div className={classNames(form.buttonContainer, style.padding)}>
                                <button
                                    type={"submit"}
                                    className={buttons.primaryButtonWithoutIcon}
                                    data-testid={testIds.workArea.mcs.editMcDialog.saveButton}
                                    disabled={!(isValid && dirty)}
                                >
                                    {t("Common.save")}
                                </button>
                                <button
                                    type={"button"}
                                    className={buttons.secondaryButtonWithoutIcon}
                                    data-testid={testIds.common.dialog.closeButton}
                                    onClick={props.handleCancel}
                                >
                                    {t("Common.cancel")}
                                </button>
                            </div>
                            <div>
                                {errors.name && (
                                    <div className={style.errorBanner}>
                                        <div className={style.warningIcon} tabIndex={0}>
                                            <FailedRedNotificationIcon
                                                backgroundColor={theme.bannerIconBackgroundColor}
                                                iconColor={theme.bannerIconColor}
                                            />
                                        </div>
                                        <p>{t("Mcs.edit.errorBanner")}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}
